import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_TV_PLAYER,
  DEFAULT_TV_STREAMING_SLUG,
  isCarePremise,
  isTvPlayerChoice,
  type PmsConfig,
  type PremiseConfig,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { useCan } from "../auth/AuthContext";
import DemoIpRangesEditor from "../components/DemoIpRangesEditor";
import SupportedLocalesCard from "../components/SupportedLocalesCard";
import OpeningHoursFields from "../components/OpeningHoursFields";
import CallPolicyEditor from "../components/CallPolicyEditor";
import { PageHeader } from "../components/PageHeader";

interface Props {
  premise: PremiseConfig;
  /** Updates the editor's parent draft - everything on this page saves through
   *  the single top-bar Save, so every edit flows up here. */
  onPremiseChange: (next: PremiseConfig) => void;
}

/**
 * What the fleet says is actually INSTALLED, for the "External app" option.
 *
 * The version is not a setting and cannot be one: the slug is global config,
 * but the version is whatever each panel happens to have, reported on its own
 * heartbeat. So this asks the fleet rather than inventing a field, and it is
 * deliberately allowed to answer "nothing".
 *
 * ⚠️ Android only (TIZEN-13). A Samsung panel has no shell to ask, so it
 * reports null there — which must read as "this platform cannot answer",
 * never as "the app is missing".
 */
function useReportedStreamingVersion(premiseId: string | undefined): {
  version: string | null;
  reporting: number;
  total: number;
} {
  const [state, setState] = useState({
    version: null as string | null,
    reporting: 0,
    total: 0,
  });
  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    void (async () => {
      try {
        const res = await foyerApi.fleetDevices({ premiseId });
        if (!alive) return;
        const devices = res?.devices ?? [];
        // Most prevalent wins. A property mid-rollout genuinely has two
        // versions, and showing the commonest is more useful than showing the
        // first one the query happened to return.
        const counts = new Map<string, number>();
        for (const d of devices) {
          const v = (d.live?.streamingAppVersion ?? "").trim();
          if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        let best: string | null = null;
        let bestN = 0;
        for (const [v, n] of counts) {
          if (n > bestN) {
            best = v;
            bestN = n;
          }
        }
        const reporting = [...counts.values()].reduce((a, b) => a + b, 0);
        setState({ version: best, reporting, total: devices.length });
      } catch {
        // A fleet read that fails must not break the config page. The option
        // simply loses its version suffix.
        if (alive) setState({ version: null, reporting: 0, total: 0 });
      }
    })();
    return () => {
      alive = false;
    };
  }, [premiseId]);
  return state;
}

/**
 * The sentence under the selector. It has one job beyond describing the
 * choice: never let the operator believe a flip has landed when it has not.
 */
function tvPlayerHelp(reported: {
  version: string | null;
  reporting: number;
  total: number;
}): string {
  const base =
    "What the TV tile opens in every room at this property. " +
    "Takes effect on the next config push - a television that has not received it keeps the player it has, which is why rolling back is safe.";
  if (reported.version) {
    return `${base} The external app's package is set once, globally, under System - settings; the version above is what ${reported.reporting} of ${reported.total} devices here report installed.`;
  }
  if (reported.total > 0) {
    // Deliberately not "the app is missing": Samsung panels cannot answer at
    // all, and saying so is the difference between a shrug and a truck roll.
    return `${base} No device here reports an installed version - Android panels report one on their next heartbeat, Samsung panels have no shell to ask.`;
  }
  return base;
}

function emptyPms(): PmsConfig {
  return {
    vendor: null,
    dailyReset: false,
    scheduledCheckInOut: { enabled: false, schedule: {} },
    messagePowerOn: false,
    defaultScheme: null,
  };
}

export default function HospitalityConfigPage({
  premise,
  onPremiseChange,
}: Props) {
  const reported = useReportedStreamingVersion(premise.premiseId);
  // The TV-player lever is staff-only: it decides which application a fielded
  // television opens, and the built-in destination is not ready for a paying
  // customer to select. `canManageSettings` is `adminOnly`, which is the
  // property we want - not merely "can edit this premise".
  const canManagePlatform = useCan("canManageSettings");
  // The slug IS global config, injected into every premise read by
  // `api/foyer.ts`. The version is not, and is asked of the fleet above.
  const slug = premise.tvStreamingAppSlug || DEFAULT_TV_STREAMING_SLUG;
  const externalLabel = reported.version
    ? `External app — ${slug} (v${reported.version})`
    : `External app — ${slug}`;
  return (
    <Stack spacing={3} data-testid="hospitality-config-page">
      <PageHeader title="Config" />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Name &amp; ID
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Display name"
              value={premise.premiseName}
              onChange={(e) =>
                onPremiseChange({ ...premise, premiseName: e.target.value })
              }
              fullWidth
              size="small"
              placeholder="Hotel Hasselbacken"
              inputProps={{ "data-testid": "premise-name-edit" }}
            />
            <TextField
              label="Premise ID (slug)"
              value={premise.premiseId}
              disabled
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "ui-monospace, Menlo, monospace",
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/*
        Location SETTINGS, not the dashboard.
        A premise owner logs in and lands on the dashboard; it is a read-at-a-
        glance surface, and burying a form somebody fills in once at setup
        inside it hides the form and clutters the glance. Opening hours belong
        with the other things about this place that are configured and then
        left alone.
      */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Opening hours &amp; footfall
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Read in this location&apos;s timezone
            {premise.address?.timezone ? ` (${premise.address.timezone})` : ""}.
            Advertising and scheduling both use these.
          </Typography>
          <OpeningHoursFields
            hours={premise.openingHours}
            audience={premise.audience}
            onChange={(next) => onPremiseChange({ ...premise, ...next })}
          />
        </CardContent>
      </Card>

      {/* The Hotel / Care / Hospital picker used to live here. A place is now
          just a place - what it IS follows from the products it has, so Care
          is switched on under Products and `segment` is derived from it. */}

      {isCarePremise(premise) && (
        <Box data-testid="video-calling-config">
          <Typography variant="h6" gutterBottom>
            Video calls
          </Typography>
          <CallPolicyEditor premise={premise} onChange={onPremiseChange} />
        </Box>
      )}

      {/* PMS-driven: the check-in message is what wakes the TV. Care premises
          have no PMS bound, so the switch would be a dead control there. */}
      {!isCarePremise(premise) && (
        <Card variant="outlined">
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={!!premise.pmsConfig?.messagePowerOn}
                  onChange={(e) =>
                    onPremiseChange({
                      ...premise,
                      pmsConfig: {
                        ...(premise.pmsConfig ?? emptyPms()),
                        messagePowerOn: e.target.checked,
                      },
                    })
                  }
                  inputProps={
                    { "data-testid": "cfg-message-power-on" } as Record<
                      string,
                      string
                    >
                  }
                />
              }
              label="Enable message power on"
            />
          </CardContent>
        </Card>
      )}

      {/* TV-6. The one lever that decides what pressing TV does at this
          property. A named select rather than a switch: both options are
          products with names, and "Streaming app: off" would tell an operator
          nothing about what they would get instead.

          Shown on every premise, care included - a care room's TV screen
          launches live TV the same way a hotel room's does (CareScheme's
          TvLaunch) - and above the locale card because it is a bigger decision
          than any of them.

          🚨 ADMIN ONLY, and the Foyer option is labelled TESTING ONLY.

          Both of those are load-bearing, and the label was briefly dropped
          before the player was ready. Where things actually stand (2026-08-17):
          the built-in destination is wired only on `feat/tv-demo-spine`, Foyer
          production holds zero `packages` rows, and the launcher
          templates mount `TvHome` without `onTune` - so a premise flipped to
          "foyer" today gets an empty television, and the one path that does
          reach a guide has no route to VOD.

          Policy (ruled 2026-08-17): a new premise auto-sets to the external
          app, only staff may flip it, and it stays in the CMS because testing
          needs it. That makes it a platform lever, not a customer setting -
          hence `canManageSettings`, which is `adminOnly`.

          ⚠️ WHEN THE BUILT-IN PLAYER SHIPS: drop "(testing only)" from the
          option and the last sentence of the helper text, and reconsider the
          gate - in the same commit that fixes `onTune` and the VOD route. */}
      {canManagePlatform && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Live TV
            </Typography>
            <TextField
              select
              label="Opens with"
              value={
                isTvPlayerChoice(premise.tvPlayer)
                  ? premise.tvPlayer
                  : DEFAULT_TV_PLAYER
              }
              onChange={(e) =>
                onPremiseChange({
                  ...premise,
                  tvPlayer: isTvPlayerChoice(e.target.value)
                    ? e.target.value
                    : DEFAULT_TV_PLAYER,
                })
              }
              fullWidth
              size="small"
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "cfg-tv-player" }}
              helperText={tvPlayerHelp(reported)}
            >
              <option value="norigin">{externalLabel}</option>
              <option value="foyer">
                Native app (Foyer shell) — testing only
              </option>
            </TextField>
          </CardContent>
        </Card>
      )}

      <SupportedLocalesCard premise={premise} onChange={onPremiseChange} />

      <DemoIpRangesEditor premise={premise} onChange={onPremiseChange} />
    </Stack>
  );
}
